const STORAGE_KEY = 'accountSessions';
const ACTIVE_KEY = 'activeAccountId';

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
}

export function loadSessions() {
  const raw = localStorage.getItem(STORAGE_KEY);
  const sessions = safeJsonParse(raw, []);
  return Array.isArray(sessions) ? sessions : [];
}

export function saveSessions(sessions) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions || []));
  window.dispatchEvent(new Event('accountSessionsChanged'));
}

export function getActiveAccountId() {
  return localStorage.getItem(ACTIVE_KEY) || null;
}

export function setActiveAccountId(accountId) {
  if (!accountId) {
    localStorage.removeItem(ACTIVE_KEY);
  } else {
    localStorage.setItem(ACTIVE_KEY, String(accountId));
  }
  window.dispatchEvent(new Event('accountSessionsChanged'));
}

export function getActiveSession() {
  const sessions = loadSessions();
  const activeId = getActiveAccountId();
  if (!activeId) return null;
  return sessions.find((s) => String(s.id) === String(activeId)) || null;
}

export function upsertSession(session) {
  const sessions = loadSessions();
  const id = session?.id;
  if (!id) return sessions;

  const next = sessions.filter((s) => String(s.id) !== String(id));
  next.unshift(session);
  saveSessions(next);
  return next;
}

export function removeSession(accountId) {
  const sessions = loadSessions();
  const next = sessions.filter((s) => String(s.id) !== String(accountId));
  saveSessions(next);

  const activeId = getActiveAccountId();
  if (activeId && String(activeId) === String(accountId)) {
    const fallback = next[0]?.id || null;
    setActiveAccountId(fallback);
    syncLegacyStorageFromActive();
  }

  return next;
}

export function clearAllSessions() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ACTIVE_KEY);
  localStorage.removeItem('userInfo');
  window.dispatchEvent(new Event('accountSessionsChanged'));
}

export function buildSessionFromLoginResponse(data) {
  const user = data?.user || null;
  const doctor = data?.doctor || null;
  const role = data?.role || user?.role || null;

  const id = data?.account_id || user?.id;
  if (!id) return null;

  return {
    id,
    user,
    doctor,
    role,
    addedAt: new Date().toISOString(),
  };
}

export function syncLegacyStorageFromActive() {
  const active = getActiveSession();
  if (!active) {
    localStorage.removeItem('userInfo');
    window.dispatchEvent(new Event('accountSessionsChanged'));
    return;
  }

  localStorage.setItem(
    'userInfo',
    JSON.stringify({
      user: active.user,
      doctor: active.doctor,
      role: active.role,
    })
  );

  window.dispatchEvent(new Event('accountSessionsChanged'));
}

export function setActiveSession(accountId) {
  setActiveAccountId(accountId);
  syncLegacyStorageFromActive();
}

export function hasAnySession() {
  return loadSessions().length > 0;
}

export function migrateLegacyUserInfoIfNeeded() {
  const sessions = loadSessions();
  if (sessions.length > 0) return;

  const legacyRaw = localStorage.getItem('userInfo');
  const legacy = safeJsonParse(legacyRaw, null);
  if (!legacy || !legacy.user || !legacy.user.id) return;

  const session = {
    id: legacy.user.id,
    user: legacy.user,
    doctor: legacy.doctor || null,
    role: legacy.role || legacy.user.role || null,
    addedAt: new Date().toISOString(),
  };

  saveSessions([session]);
  setActiveAccountId(session.id);
  syncLegacyStorageFromActive();
}
